import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import lierac from "@/assets/brands/lierac.webp";
import soraya from "@/assets/brands/soraya.webp";
import bielenda from "@/assets/brands/bielenda.webp";
import dermika from "@/assets/brands/dermika.webp";
import skinarte from "@/assets/brands/skinarte.webp";
import sugarbearhair from "@/assets/brands/sugarbearhair.webp";
import frudia from "@/assets/brands/frudia.webp";
import sheaMoisture from "@/assets/brands/shea-moisture.webp";

const brands = [
  { name: "Bielenda", filter: "Bielenda SupremeLab", src: bielenda, origin: { en: "Poland", ar: "بولندا" } },
  { name: "Dermika", filter: "Dermika", src: dermika, origin: { en: "Poland", ar: "بولندا" } },
  { name: "Soraya", filter: "Soraya", src: soraya, origin: { en: "Poland", ar: "بولندا" } },
  { name: "Lierac", filter: "Lierac", src: lierac, origin: { en: "France", ar: "فرنسا" } },
  { name: "SkinArté", filter: "SkinArté", src: skinarte, origin: { en: "Italy", ar: "إيطاليا" } },
  { name: "Frudia", filter: "Frudia", src: frudia, origin: { en: "Korea", ar: "كوريا" } },
  { name: "Shea Moisture", filter: "Shea Moisture", src: sheaMoisture, origin: { en: "USA", ar: "أمريكا" } },
  { name: "SugarBearHair", filter: "SugarBearHair", src: sugarbearhair, origin: { en: "USA", ar: "أمريكا" } },
];

const BrandsSection = () => {
  const { lang } = useLanguage();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const fontFamily = lang === "ar" ? "'Noto Sans Arabic', 'Nunito', sans-serif" : "'Nunito', sans-serif";

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, []);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8 * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section className="bg-white px-4 sm:px-6 py-12 sm:py-20 lg:px-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 sm:mb-12 flex items-end justify-between gap-4">
          <h2
            className="text-2xl sm:text-4xl font-bold overflow-visible"
            style={{
              fontFamily: "'Poppins', sans-serif",
              background: "linear-gradient(90deg, #E88CA4 0%, #a8405c 30%, #f8c8d8 60%, #E88CA4 100%)",
              backgroundSize: "300% 100%",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              WebkitTextFillColor: "transparent",
              animation: "gradientShift 4s ease infinite",
              lineHeight: 1.4,
              paddingTop: "0.15em",
            }}
          >
            {lang === "ar" ? "الماركات" : "Brands"}
          </h2>

          {/* Arrows — hidden on mobile (swipe) */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!canLeft}
              aria-label="Previous"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E88CA4]/40 bg-white text-[#E88CA4] transition-all duration-200 hover:bg-gradient-to-b hover:from-[#E88CA4] hover:to-[#d26e8c] hover:text-white hover:border-transparent hover:shadow-[0_4px_12px_rgba(232,140,164,0.35)] disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#E88CA4] disabled:cursor-not-allowed active:scale-[0.97]"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!canRight}
              aria-label="Next"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E88CA4]/40 bg-white text-[#E88CA4] transition-all duration-200 hover:bg-gradient-to-b hover:from-[#E88CA4] hover:to-[#d26e8c] hover:text-white hover:border-transparent hover:shadow-[0_4px_12px_rgba(232,140,164,0.35)] disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#E88CA4] disabled:cursor-not-allowed active:scale-[0.97]"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="relative">
          {/* Fade edges */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-white to-transparent" />

          <div
            ref={scrollerRef}
            className="flex gap-3 sm:gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-4 px-4 sm:mx-0 sm:px-1"
            style={{ scrollbarWidth: "none" }}
          >
            <style>{`section .overflow-x-auto::-webkit-scrollbar{display:none}`}</style>
            {brands.map((brand) => (
              <Link
                key={brand.name}
                to={`/shop?brand=${encodeURIComponent(brand.filter)}`}
                className="snap-start shrink-0 w-[44%] sm:w-[28%] lg:w-[22%] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E88CA4] rounded-2xl"
              >
                <div className="group relative rounded-2xl bg-white border border-neutral-200/80 hover:border-[#E88CA4]/40 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(232,140,164,0.18)] transition-all duration-300 p-5 sm:p-6 flex flex-col items-center justify-center aspect-[4/5] cursor-pointer">
                  <div className="flex-1 w-full flex items-center justify-center">
                    <img
                      src={brand.src}
                      alt={brand.name}
                      className="max-h-12 sm:max-h-16 w-auto object-contain grayscale opacity-70 transition-all duration-500 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105"
                      draggable={false}
                    />
                  </div>
                  <div className="text-center">
                    <h3
                      className="text-sm sm:text-base font-bold text-neutral-800"
                      style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      {brand.name}
                    </h3>
                    <p
                      className="mt-0.5 text-[10px] sm:text-xs text-neutral-500 uppercase tracking-wider"
                      style={{ fontFamily }}
                    >
                      {lang === "ar" ? brand.origin.ar : brand.origin.en}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandsSection;
